import { clsx } from 'clsx';
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, ChevronsUpDown, Inbox } from 'lucide-react';
import type { ReactNode } from 'react';
import { EmptyState, Skeleton } from './Feedback';

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T, index: number) => ReactNode;
  /** Server tomonida saralash maydoni (odatda `key` bilan bir xil). */
  sortKey?: string;
  align?: 'left' | 'right' | 'center';
  width?: number | string;
  className?: string;
  /** Raqamli ustun: o'ngga, tabular raqamlar. */
  numeric?: boolean;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey: (row: T) => string;
  loading?: boolean;
  /** Qayta yuklanayotganda eski natijani xira ko'rsatish (sakrashsiz). */
  refetching?: boolean;
  onRowClick?: (row: T) => void;
  /** "-createdAt" kabi. */
  sort?: string;
  onSortChange?: (sort: string | undefined) => void;
  selectable?: boolean;
  selected?: string[];
  onSelectedChange?: (ids: string[]) => void;
  empty?: ReactNode;
  footer?: ReactNode;
  className?: string;
  rowTone?: (row: T) => 'danger' | 'warning' | undefined;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  refetching,
  onRowClick,
  sort,
  onSortChange,
  selectable,
  selected = [],
  onSelectedChange,
  empty,
  footer,
  className,
}: DataTableProps<T>) {
  const sortField = sort?.replace(/^-/, '');
  const sortDesc = sort?.startsWith('-');
  const allIds = rows?.map(rowKey) ?? [];
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.includes(id));

  const toggleSort = (col: Column<T>) => {
    if (!onSortChange || !col.sortKey) return;
    if (sortField !== col.sortKey) onSortChange(`-${col.sortKey}`);
    else if (sortDesc) onSortChange(col.sortKey);
    else onSortChange(undefined);
  };

  return (
    <div className={className}>
      <div className="kts-table-wrap" style={{ opacity: refetching ? 0.55 : 1, transition: 'opacity .2s' }}>
        <table className="kts-table">
          <thead>
            <tr>
              {selectable && (
                <th style={{ width: 44 }}>
                  <label className="kts-check">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      aria-label="Hammasini tanlash"
                      onChange={() => onSelectedChange?.(allSelected ? [] : allIds)}
                    />
                  </label>
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={{ width: col.width, textAlign: col.numeric ? 'right' : col.align }}
                  data-sortable={col.sortKey && onSortChange ? 'true' : undefined}
                  onClick={() => toggleSort(col)}
                  aria-sort={sortField === col.sortKey ? (sortDesc ? 'descending' : 'ascending') : undefined}
                >
                  {col.header}
                  {col.sortKey && onSortChange && (
                    <span className="sort">
                      {sortField === col.sortKey ? (
                        sortDesc ? (
                          <ArrowDown size={13} />
                        ) : (
                          <ArrowUp size={13} />
                        )
                      ) : (
                        <ChevronsUpDown size={13} />
                      )}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && !rows
              ? Array.from({ length: 6 }, (_, i) => (
                  <tr key={`sk${i}`}>
                    {selectable && <td />}
                    {columns.map((c) => (
                      <td key={c.key}>
                        <Skeleton height={14} width={c.numeric ? 70 : '70%'} style={{ marginLeft: c.numeric ? 'auto' : undefined }} />
                      </td>
                    ))}
                  </tr>
                ))
              : rows?.map((row, i) => {
                  const id = rowKey(row);
                  const isSel = selected.includes(id);
                  return (
                    <tr
                      key={id}
                      data-clickable={onRowClick ? 'true' : undefined}
                      data-selected={isSel ? 'true' : undefined}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                    >
                      {selectable && (
                        <td onClick={(e) => e.stopPropagation()}>
                          <label className="kts-check">
                            <input
                              type="checkbox"
                              checked={isSel}
                              aria-label="Tanlash"
                              onChange={() => onSelectedChange?.(isSel ? selected.filter((s) => s !== id) : [...selected, id])}
                            />
                          </label>
                        </td>
                      )}
                      {columns.map((col) => (
                        <td
                          key={col.key}
                          className={clsx(col.numeric && 'num', col.className)}
                          style={{ textAlign: col.numeric ? 'right' : col.align }}
                        >
                          {col.cell(row, i)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
          </tbody>
        </table>
        {!loading && rows && rows.length === 0 && (empty ?? <EmptyState icon={<Inbox />} title="Hozircha ma’lumot yo‘q" />)}
      </div>
      {footer}
    </div>
  );
}

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizes = [25, 50, 100],
}: {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (p: number) => void;
  onPageSizeChange?: (s: number) => void;
  pageSizes?: number[];
}) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  const nums: (number | '…')[] = [];
  for (let p = 1; p <= pages; p++) {
    if (p === 1 || p === pages || Math.abs(p - page) <= 1) nums.push(p);
    else if (nums[nums.length - 1] !== '…') nums.push('…');
  }
  return (
    <div className="kts-table-footer">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span className="tabular">
          {from}–{to} / {total.toLocaleString('ru-RU').replace(/,/g, ' ')}
        </span>
        {onPageSizeChange && (
          <select
            className="kts-select"
            style={{ height: 30, width: 'auto', fontSize: 12.5, paddingRight: 30 }}
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            aria-label="Sahifadagi qatorlar"
          >
            {pageSizes.map((s) => (
              <option key={s} value={s}>
                {s} ta
              </option>
            ))}
          </select>
        )}
      </div>
      <nav className="kts-pagination" aria-label="Sahifalar">
        <button type="button" disabled={page <= 1} onClick={() => onPageChange(page - 1)} aria-label="Oldingi">
          <ChevronLeft size={15} />
        </button>
        {nums.map((n, i) =>
          n === '…' ? (
            <span key={`e${i}`} style={{ padding: '0 4px' }}>
              …
            </span>
          ) : (
            <button key={n} type="button" aria-current={n === page ? 'page' : undefined} onClick={() => onPageChange(n)}>
              {n}
            </button>
          ),
        )}
        <button type="button" disabled={page >= pages} onClick={() => onPageChange(page + 1)} aria-label="Keyingi">
          <ChevronRight size={15} />
        </button>
      </nav>
    </div>
  );
}
